import { Spinner, useVideoTimeline, type PlaybackTransport } from "@carbon6/c6-frontend-design";
import { lazy, Suspense, useState } from "react";
import type { ViewerMode, ViewerTrack } from "../domain/viewerTypes";
import { samplePoseAt, SAMPLE_STATUS_LABELS } from "../feature/poseSampling";
import styles from "./Viewer.module.css";
import type { TcpInspection } from "../feature/tcpPose";

const TrajectoryViewer = lazy(() =>
  import("./TrajectorySimViewer").then((m) => ({ default: m.TrajectorySimViewer })),
);
const FrameViewer = lazy(() =>
  import("./FrameCompareViewer").then((m) => ({ default: m.FrameCompareViewer })),
);

export type ViewerProps = {
  inspection?: TcpInspection | null;
  tracks: readonly ViewerTrack[];
  transport: PlaybackTransport;
  active: boolean;
  mode: ViewerMode;
  className?: string;
  meshOpacity?: number;
  showSampleStatus?: boolean;
  presentation?: "surface" | "overlay";
};

/** 화면은 엔진을 알지 않는다. 트랙 선택과 비활성 처리를 이 경계에서 함께 관리한다. */
export function Viewer({
  active,
  className,
  mode,
  tracks,
  transport,
  meshOpacity = 1,
  showSampleStatus = true,
  presentation = "surface",
  inspection = null,
}: ViewerProps) {
  const seconds = useVideoTimeline(transport, (s) => s.currentTimeSeconds);
  const [selection, setSelection] = useState<string | null>(null);
  const selectedTrackId = tracks.some((track) => track.id === selection) ? selection : null;
  const props = {
    inspection,
    active,
    tracks,
    playheadSeconds: seconds,
    frameClock: transport.frameClock,
    selectedTrackId,
    onSelectTrack: setSelection,
    meshOpacity,
    presentation,
  };
  return (
    <section
      className={[styles.viewer, className].filter(Boolean).join(" ")}
      aria-label="3D 점검"
      data-sample-status={showSampleStatus ? "visible" : "hidden"}
    >
      {selectedTrackId === null ? null : (
        <div className={styles.toolbar}>
          <span>선택: {tracks.find((track) => track.id === selectedTrackId)?.label}</span>
        </div>
      )}
      <div className={styles.surface}>
        <Suspense fallback={<Spinner label="3D 화면을 준비하는 중" />}>
          {mode === "mapping" ? <TrajectoryViewer {...props} /> : <FrameViewer {...props} />}
        </Suspense>
      </div>
      {showSampleStatus ? (
        <ul className={styles.samples} aria-label="관측 상태">
          {tracks.map((track) => {
            const sample = samplePoseAt(track.series, seconds);
            return (
              <li key={track.id} data-track={track.id} data-sample-status={sample.status}>
                {track.label}: {SAMPLE_STATUS_LABELS[sample.status]}
                {sample.row !== null &&
                  ` · 원본 행 ${sample.row + 1} · ${sample.sampleTime?.toFixed(3)}s`}
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
